import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from '../../components/AuthCard.js';
import { safeNextPath } from '../../lib/nextPath.js';
import { login } from '../../lib/session.js';
import { useSession } from '../../lib/useSession.js';

/**
 * Login único do produto.
 *
 * Antes cada tela tinha o seu — um embutido no /admin, outro no /parear — e o
 * do /admin era o que estava quebrado. Um só lugar significa um só lugar para
 * consertar, e `?next=` leva a pessoa de volta a onde ela queria estar.
 */
export function Entrar() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const next = safeNextPath(params.get('next'));

  // Já entrou (inclusive voltando pelo histórico): segue para o destino.
  if (session.signedIn) return <Navigate to={next} replace />;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      await login({ email: email.trim(), password });
      navigate(next, { replace: true });
    } catch (err) {
      // A mensagem vem do servidor e já é texto pronto para o operador —
      // inclusive a do bloqueio progressivo, que informa quantos segundos
      // faltam. Trocá-la por algo genérico esconderia essa informação.
      setError(err instanceof Error ? err.message : 'Não foi possível entrar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard
      title="Entrar"
      subtitle="Use a conta que recebeu de quem administra o bingo."
      /* O lugar do "Criar conta" fica previsto; o autocadastro vem depois. */
      footer={<span className="auth-note">Esqueceu a senha? Peça a quem administra.</span>}
    >
      <form className="auth-form" onSubmit={submit} noValidate>
        <label className="field">
          <span>E-mail</span>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            inputMode="email"
            required
            autoFocus
            disabled={busy}
          />
        </label>

        <label className="field">
          <span>Senha</span>
          <input
            type="password"
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            disabled={busy}
          />
        </label>

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn primary big block" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </AuthCard>
  );
}
